import {
  type CreateRequestBodyInput,
  CreateRequestBodySchema,
  FreeScoutCreateResponseSchema,
  FreeScoutCreateSchema,
  FreeScoutLinkSchema,
  FreeScoutMappingListSchema,
  type FreeScoutMappingSchema,
  type FreeScoutPreview,
  FreeScoutPreviewSchema,
  type FreeScoutReference,
  FreeScoutReferenceSchema,
  FreeScoutRepairSchema,
  FreeScoutRequesterListSchema,
  type Me,
  type StaffRequestDetail,
} from "@hoffman-support/contracts";
import { z } from "zod";
import { ApiError, type ApiFailure } from "./api";
import { staffGet } from "./staff-api";

export type FreeScoutRequester = z.infer<typeof FreeScoutRequesterListSchema>["requesters"][number];
export type FreeScoutMapping = z.infer<typeof FreeScoutMappingSchema>;
export type HandoffSelection = {
  requester: FreeScoutRequester | null;
  sourceThreadId: number | null;
  verification: string;
};
export const emptySelection = (): HandoffSelection => ({
  requester: null,
  sourceThreadId: null,
  verification: "",
});

export function canUseFreeScout(me: Me) {
  return me.user.roles.some((role) => role === "staff" || role === "admin");
}

export function freeScoutRoute(path: string): FreeScoutReference | null {
  const match = path.match(/^\/staff\/freescout\/([^/]+)\/([0-9]+)\/([1-9][0-9]*)\/?$/);
  if (!match) return null;
  const parsed = FreeScoutReferenceSchema.safeParse({
    instanceId: match[1],
    mailboxId: Number(match[2]),
    conversationId: Number(match[3]),
  });
  return parsed.success ? parsed.data : null;
}

function referencePath(reference: FreeScoutReference) {
  const { instanceId, mailboxId, conversationId } = reference;
  const value = FreeScoutReferenceSchema.parse({ instanceId, mailboxId, conversationId });
  return `/api/admin/freescout/${value.instanceId}/${value.mailboxId}/${value.conversationId}`;
}

export function freeScoutUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      url.hostname === "admin.opsolutions.io" &&
      !url.port &&
      !url.username &&
      !url.password &&
      url.pathname.startsWith("/conversation/")
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export function handoffReady(preview: FreeScoutPreview, selection: HandoffSelection) {
  return Boolean(
    selection.requester &&
      preview.sourceThreads.some((thread) => thread.id === selection.sourceThreadId) &&
      selection.verification.trim().length >= 10 &&
      selection.verification.trim().length <= 500,
  );
}

function refuse(message: string): never {
  throw new ApiError({ status: 400, code: "handoff_not_ready", message });
}

function verifiedSelection(preview: FreeScoutPreview, selection: HandoffSelection) {
  if (!selection.requester) refuse("Find and select the verified portal account first.");
  if (!preview.sourceThreads.some((thread) => thread.id === selection.sourceThreadId))
    refuse("Choose a published customer message from this conversation.");
  const verification = selection.verification.trim();
  if (verification.length < 10 || verification.length > 500)
    refuse("Record how you verified the requester, using 10 to 500 characters.");
  return {
    requester: selection.requester,
    sourceThreadId: selection.sourceThreadId as number,
    verification,
  };
}

function unlinked(preview: FreeScoutPreview) {
  if (preview.status.syncState !== "unlinked" || preview.status.requests.length)
    refuse("This conversation already has a link to review. Open the linked request first.");
}

function command<T>(schema: z.ZodType<T>, value: unknown): T {
  const parsed = schema.safeParse(value);
  if (!parsed.success) {
    throw new ApiError({
      status: 400,
      code: "invalid_body",
      message: "Some fields need attention.",
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path
          .map(String)
          .join(".")
          .replace(/^request\./, ""),
        message: issue.message,
      })),
    });
  }
  return parsed.data;
}

export function freeScoutCreateBody(
  preview: FreeScoutPreview,
  selection: HandoffSelection,
  body: CreateRequestBodyInput,
) {
  unlinked(preview);
  const verified = verifiedSelection(preview, selection);
  const request = command(CreateRequestBodySchema, body);
  return command(FreeScoutCreateSchema, {
    requesterUserId: verified.requester.id,
    sourceThreadId: verified.sourceThreadId,
    request: {
      ...request,
      caller: {
        upn: verified.requester.upn,
        ...(verified.requester.displayName ? { displayName: verified.requester.displayName } : {}),
        verification: verified.verification,
      },
    },
  });
}

type RequestOwner = Pick<StaffRequestDetail, "id" | "publicId" | "requester">;
export function freeScoutLinkBody(
  preview: FreeScoutPreview,
  selection: HandoffSelection,
  request: RequestOwner,
) {
  unlinked(preview);
  const verified = verifiedSelection(preview, selection);
  if (request.requester.id !== verified.requester.id)
    refuse("The request belongs to a different portal account. Check the requester and reference.");
  return command(FreeScoutLinkSchema, {
    requestId: request.id,
    requesterUserId: verified.requester.id,
    sourceThreadId: verified.sourceThreadId,
    verification: verified.verification,
  });
}

export function freeScoutRepairBody(
  mapping: FreeScoutMapping,
  preview: FreeScoutPreview,
  selection: HandoffSelection,
  requesterId: string | null,
) {
  const verified = verifiedSelection(preview, selection);
  if (verified.requester.id !== requesterId)
    refuse("Select the verified account that owns this request.");
  if (preview.status.requests.some((request) => request.id !== mapping.requestId))
    refuse("That conversation is already linked to a different request.");
  return command(FreeScoutRepairSchema, {
    instanceId: preview.instanceId,
    mailboxId: preview.mailboxId,
    conversationId: preview.conversationId,
    sourceThreadId: verified.sourceThreadId,
    requesterUserId: verified.requester.id,
    version: mapping.version,
    verification: verified.verification,
  });
}

async function parse<T>(response: Response, schema: z.ZodType<T>): Promise<T> {
  if (!response.ok) {
    let failure: ApiFailure = {
      status: response.status,
      code: "unknown",
      message: "The request could not be completed.",
    };
    try {
      const body = (await response.json()) as {
        error?: { code?: string; message?: string };
        issues?: ApiFailure["issues"];
      };
      failure = {
        ...failure,
        code: body.error?.code ?? failure.code,
        message: body.error?.message ?? failure.message,
        ...(body.issues
          ? {
              issues: body.issues.map((issue) => ({
                ...issue,
                path: issue.path.replace(/^request\./, ""),
              })),
            }
          : {}),
      };
    } catch {
      /* Keep the safe generic message for non-JSON failures. */
    }
    throw new ApiError(failure);
  }
  return schema.parse(await response.json());
}

function write(path: string, me: Me, body: unknown, idempotencyKey?: string) {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      "x-csrf-token": me.csrfToken,
      ...(idempotencyKey ? { "idempotency-key": idempotencyKey } : {}),
    },
    body: JSON.stringify(body),
  });
}

export function freeScoutPreview(reference: FreeScoutReference) {
  return fetch(referencePath(reference), { credentials: "same-origin" }).then((res) =>
    parse(res, FreeScoutPreviewSchema),
  );
}

export function freeScoutRequesters(query: string) {
  return fetch(`/api/admin/freescout/requesters?q=${encodeURIComponent(query.trim())}`, {
    credentials: "same-origin",
  }).then((res) => parse(res, FreeScoutRequesterListSchema));
}

export async function findFreeScoutRequest(publicId: string, requesterId: string) {
  const reference = publicId.trim().toUpperCase();
  if (!/^HOF-[0-9]+$/.test(reference)) refuse("Enter the request reference, such as HOF-000123.");
  const request = await staffGet(reference);
  if (request.requester.id !== requesterId)
    refuse("The request belongs to a different portal account. Check the requester and reference.");
  return request;
}

export function freeScoutCreate(
  me: Me,
  idempotencyKey: string,
  preview: FreeScoutPreview,
  selection: HandoffSelection,
  body: CreateRequestBodyInput,
) {
  return write(
    `${referencePath(preview)}/requests`,
    me,
    freeScoutCreateBody(preview, selection, body),
    idempotencyKey,
  ).then((res) => parse(res, FreeScoutCreateResponseSchema));
}

export function freeScoutLink(
  me: Me,
  preview: FreeScoutPreview,
  selection: HandoffSelection,
  request: RequestOwner,
) {
  return write(
    `${referencePath(preview)}/link`,
    me,
    freeScoutLinkBody(preview, selection, request),
  ).then((res) => parse(res, z.object({ linked: z.literal(true) }).strict()));
}

export function freeScoutMappings(requestId: string) {
  z.uuid().parse(requestId);
  return fetch(`/api/admin/requests/${encodeURIComponent(requestId)}/freescout`, {
    credentials: "same-origin",
  }).then((res) => parse(res, FreeScoutMappingListSchema));
}

export function freeScoutRepair(
  me: Me,
  mapping: FreeScoutMapping,
  preview: FreeScoutPreview,
  selection: HandoffSelection,
  requesterId: string | null,
) {
  z.uuid().parse(mapping.id);
  return write(
    `/api/admin/freescout/mappings/${encodeURIComponent(mapping.id)}/repair`,
    me,
    freeScoutRepairBody(mapping, preview, selection, requesterId),
  ).then((res) => parse(res, z.object({ repaired: z.literal(true) }).strict()));
}

export function handoffError(error: unknown, fallback: string) {
  if (!(error instanceof ApiError)) return fallback;
  if (error.failure.status === 409)
    return "The link changed while you were working. Reload the conversation and review it before trying again.";
  return error.failure.message;
}
